import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useState, type JSX } from "react";

import { DatePicker } from "../../../../components/ui/date-picker.js";
import { TimePicker } from "../../../../components/ui/time-picker.js";
import { PLATFORM_LABELS } from "../../types.js";
import type { ScheduledPost } from "../../types.js";
import { isoToLocalParts, localPartsToIso, localTime } from "../calendar-utils.js";

/**
 * Reschedule dialog — date + time pickers prefilled from the post's current
 * slot. Saves the combined LOCAL date/time as a UTC ISO string. Always mounted
 * (open toggles on `post`) so its hooks run unconditionally; the buffers
 * re-sync via effect whenever a different post opens it. Extracted from
 * `ScheduleCalendar.tsx`.
 */
export function RescheduleDialog({
  post,
  busy,
  onClose,
  onSave,
}: {
  post: ScheduledPost | null;
  busy: boolean;
  onClose: () => void;
  onSave: (scheduledForIso: string) => void;
}): JSX.Element {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");

  useEffect(() => {
    if (!post) return;
    const parts = isoToLocalParts(post.scheduled_for);
    setDate(parts.date);
    setTime(parts.time);
  }, [post?.id, post?.scheduled_for]);

  const iso = localPartsToIso(date, time);
  const label = post ? (PLATFORM_LABELS[post.platform] ?? post.platform) : "";

  return (
    <Dialog open={post !== null} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>Reschedule post</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        {post && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {label} — currently {localTime(post.scheduled_for)}.
          </Typography>
        )}
        <DatePicker value={date} onChange={setDate} placeholder="Date" disabled={busy} />
        <Box sx={{ display: "grid", gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Time</Typography>
          <TimePicker value={time} onChange={setTime} disabled={busy} />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" size="small" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          data-testid="cal-reschedule-save"
          disabled={busy || iso === null}
          onClick={() => iso && onSave(iso)}
        >
          {busy ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
